<template>
  <!-- Q5 (PM lite) "Progress by location" from the Work Progress design, as a hierarchy:
       drawing set → drawing, each with the share of its tasks that are
       not started / in progress / completed -->
  <v-card class="drawing-card">
    <div class="head">
      <h2 class="card-title">{{ $t('taskDrawing.title') }}</h2>
      <div class="tools">
        <v-btn size="x-small" variant="text" :disabled="!rows.length || allExpanded" @click="expandAll">
          <v-icon start size="14">mdi-unfold-more-horizontal</v-icon>{{ $t('taskDrawing.expandAll') }}
        </v-btn>
        <v-btn size="x-small" variant="text" :disabled="!rows.length || !expanded.size" @click="collapseAll">
          <v-icon start size="14">mdi-unfold-less-horizontal</v-icon>{{ $t('taskDrawing.collapseAll') }}
        </v-btn>
      </div>
      <div class="legend">
        <span><span class="dot" style="background: #5C6B7A"></span>{{ $t('taskStatus.notStarted') }}</span>
        <span><span class="dot" style="background: #2196F3"></span>{{ $t('taskStatus.inProgress') }}</span>
        <span><span class="dot" style="background: #4CAF50"></span>{{ $t('taskStatus.completed') }}</span>
      </div>
    </div>

    <v-skeleton-loader v-if="!rows.length && loading" type="list-item@4" />
    <div v-else-if="!rows.length" class="empty text-medium-emphasis">{{ $t('taskDrawing.noData') }}</div>

    <div v-else class="scroll">
      <div
        v-for="node in visibleNodes"
        :key="node.key"
        class="row row--clickable"
        :class="[`row--${node.level}`, { 'is-selected': isSelected(node), 'is-dimmed': hasSelection && !isSelected(node) }]"
        :style="{ paddingLeft: `${node.level * 16}px` }"
        role="button"
        tabindex="0"
        :aria-pressed="isSelected(node)"
        @click="select(node)"
        @keydown.enter.prevent="select(node)"
      >
        <button
          type="button"
          class="toggle"
          :class="{ 'toggle--leaf': !node.children.length }"
          :disabled="!node.children.length"
          :aria-expanded="node.children.length ? expanded.has(node.key) : undefined"
          @click.stop="toggle(node)"
        >
          <v-icon v-if="node.children.length" size="14">{{ expanded.has(node.key) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
        </button>
        <span class="label" :title="`${node.title} · ${$t('taskDrawing.tasks', { n: node.total })}`">{{ node.title }}</span>
        <div class="bar">
          <div class="seg" :style="{ width: `${node.notStarted}%`, background: '#5C6B7A' }" :title="`${$t('taskStatus.notStarted')}: ${node.notStartedN}`">{{ node.notStarted >= 10 ? `${node.notStarted}%` : '' }}</div>
          <div class="seg" :style="{ width: `${node.inProgress}%`, background: '#2196F3' }" :title="`${$t('taskStatus.inProgress')}: ${node.inProgressN}`">{{ node.inProgress >= 10 ? `${node.inProgress}%` : '' }}</div>
          <div class="seg" :style="{ width: `${node.completed}%`, background: '#4CAF50' }" :title="`${$t('taskStatus.completed')}: ${node.completedN}`">{{ node.completed >= 10 ? `${node.completed}%` : '' }}</div>
        </div>
        <span class="count">{{ node.total.toLocaleString() }}</span>
      </div>
    </div>
  </v-card>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { progressByLocationSql } from '@/utils/taskTables';
import { taskSCurveFilters, taskScopeKey, taskScope, isTaskScopeReady, toggleDrawingFilter } from '@/utils/taskSCurveFilters';

const store = useStore();
const { t } = useI18n();
const conn = computed(() => store.getters.getDBConnection);
const dataLoaded = computed(() => store.getters.getDataLoaded);

// One row per (task, location); the tree is aggregated here so a task spanning several
// locations of one drawing is counted once at the drawing and once at the set. Locations are
// not shown as a level, only drawing set → drawing.
const rows = ref([]);
const loading = ref(false);
const expanded = ref(new Set());

let queryVersion = 0;

const queryRows = async (query) => {
  const result = await conn.value.query(query);
  return result.toArray().map(row => row.toJSON());
};

const loadData = async () => {
  if (!dataLoaded.value || !isTaskScopeReady.value) return;
  const version = ++queryVersion;
  loading.value = true;
  try {
    // Ignore the drawing cross-filter here: this card is where it is picked
    const result = await queryRows(progressByLocationSql(taskScope(['drawing'])));
    if (version !== queryVersion) return; // a newer request superseded this one
    rows.value = result;
    // Start with the drawing sets open and their drawings closed
    expanded.value = new Set(result.map(r => `set:${r.drawingSetId ?? ''}`));
  } catch (error) {
    if (version !== queryVersion) return;
    console.error('[TaskProgressByDrawingCard] query failed:', error.message);
    rows.value = [];
  } finally {
    if (version === queryVersion) loading.value = false;
  }
};

const makeNode = (key, level, title) => ({ key, level, title, tasks: new Map(), children: [], childIndex: new Map() });
const child = (parent, key, level, title) => {
  let node = parent.childIndex.get(key);
  if (!node) {
    node = makeNode(key, level, title);
    parent.childIndex.set(key, node);
    parent.children.push(node);
  }
  return node;
};

// Shares are rounded so the three always add up to 100: the remainder goes to Not started
const finish = (node) => {
  const statuses = [...node.tasks.values()];
  const total = statuses.length;
  const completedN = statuses.filter(s => s === 'completed').length;
  const inProgressN = statuses.filter(s => s === 'inProgress').length;
  const completed = total ? Math.round(completedN / total * 100) : 0;
  const inProgress = total ? Math.round(inProgressN / total * 100) : 0;
  Object.assign(node, {
    total,
    completedN,
    inProgressN,
    notStartedN: total - completedN - inProgressN,
    completed,
    inProgress,
    notStarted: Math.max(0, 100 - completed - inProgress),
  });
  node.children.sort((a, b) => a.title.localeCompare(b.title));
  node.children.forEach(finish);
  return node;
};

const tree = computed(() => {
  const root = makeNode('root', -1, '');
  for (const r of rows.value) {
    const setKey = `set:${r.drawingSetId ?? ''}`;
    const drawingKey = `${setKey}/drawing:${r.drawingId ?? ''}`;
    const set = child(root, setKey, 0, r.drawingSetTitle || r.drawingSetId || t('taskDrawing.noDrawingSet'));
    set.drawingSetId = r.drawingSetId ?? null;
    const drawing = child(set, drawingKey, 1, r.drawingTitle || r.drawingId || '–');
    drawing.drawingId = r.drawingId ?? null;
    set.tasks.set(r.taskId, r.status);
    drawing.tasks.set(r.taskId, r.status);
  }
  return finish(root).children;
});

// Flatten the tree, skipping the children of collapsed nodes
const visibleNodes = computed(() => {
  const out = [];
  const walk = (nodes) => {
    for (const node of nodes) {
      out.push(node);
      if (node.children.length && expanded.value.has(node.key)) walk(node.children);
    }
  };
  walk(tree.value);
  return out;
});

const expandableKeys = computed(() => tree.value.filter(n => n.children.length).map(n => n.key));
const allExpanded = computed(() => expandableKeys.value.every(k => expanded.value.has(k)));
const expandAll = () => { expanded.value = new Set(expandableKeys.value); };
const collapseAll = () => { expanded.value = new Set(); };

// Clicking a set or a drawing cross-filters the other visuals to the tasks on it
const hasSelection = computed(() => taskSCurveFilters.drawingSetIds.length > 0 || taskSCurveFilters.drawingIds.length > 0);
const isSelected = (node) => node.level === 0
  ? node.drawingSetId !== null && taskSCurveFilters.drawingSetIds[0] === node.drawingSetId
  : node.drawingId !== null && taskSCurveFilters.drawingIds[0] === node.drawingId;
const select = (node) => {
  if (node.level === 0 && node.drawingSetId !== null) toggleDrawingFilter({ drawingSetId: node.drawingSetId });
  else if (node.level === 1 && node.drawingId !== null) toggleDrawingFilter({ drawingId: node.drawingId });
};

const toggle = (node) => {
  if (!node.children.length) return;
  const next = new Set(expanded.value);
  if (next.has(node.key)) next.delete(node.key); else next.add(node.key);
  expanded.value = next;
};

onMounted(loadData);
watch(
  [dataLoaded, taskScopeKey],
  loadData,
  { deep: true }
);
</script>

<style scoped>
.drawing-card {
  min-width: 0;
  width: 100%;
  margin: 0; /* App.vue gives every v-card margin: 0 auto, which shrinks a grid item to its content */
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 20px 16px;
}
.head {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}
.card-title {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 400;
}
.tools {
  display: flex;
  gap: 2px;
  margin-left: auto;
}
.tools .v-btn {
  text-transform: none;
  letter-spacing: 0;
}
.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 11px;
  opacity: 0.85;
  flex-basis: 100%;
}
.legend > span {
  display: flex;
  align-items: center;
  gap: 5px;
}
.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
.empty {
  font-size: 14px;
  padding: 8px 0;
}
.scroll {
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow: auto;
  max-height: 300px;
}
.row {
  display: grid;
  grid-template-columns: 20px minmax(90px, 150px) minmax(0, 1fr) 36px;
  gap: 8px;
  align-items: center;
}
.row--clickable {
  cursor: pointer;
  border-radius: 3px;
  transition: opacity 0.2s ease, background-color 0.2s ease;
}
.row--clickable:hover {
  background: var(--tk-row-hover);
}
.is-selected {
  background: var(--tk-row-hover);
  outline: 1px solid var(--tk-info);
  outline-offset: 1px;
}
.is-dimmed {
  opacity: 0.5;
}
.row--0 .label {
  font-weight: 500;
  opacity: 1;
}
.toggle {
  width: 20px;
  height: 20px;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 3px;
}
.toggle:hover:not(:disabled) {
  background: var(--tk-row-hover);
}
.toggle--leaf {
  cursor: default;
}
.label {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.85;
}
.bar {
  display: flex;
  height: 18px;
  border-radius: 2px;
  overflow: hidden;
  background: var(--tk-track);
  font-size: 11px;
  font-weight: 500;
  color: #fff;
}
.seg {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  white-space: nowrap;
  transition: width 0.4s ease;
}
.count {
  font-size: 11px;
  text-align: right;
  opacity: 0.6;
  font-variant-numeric: tabular-nums;
}
</style>
